import React, { Component } from 'react';
import axios from '../../../api/axios';
import FormGroupWrapper from '../FormGroupWrapper';
import ModalBody from '../modal/ModalBody';
import ModalControls from '../modal/ModalControls';
import ModalFooter from '../modal/ModalFooter';
import ModalHeader from '../modal/ModalHeader';

type Props = {
  onCloseModal: () => void;
  disableButtons?: boolean;
};

type State = {
  ip1File: File | null;
  bolovanjeFile: File | null;
  obracunFile: File | null;
  isLoading: boolean;
};

class PayrollGeneratorModal extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      ip1File: null,
      bolovanjeFile: null,
      obracunFile: null,
      isLoading: false,
    };
  }

  handleFileChange = (fileType: 'ip1' | 'bolovanje' | 'obracun') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (fileType === 'ip1') {
      this.setState({ ip1File: file });
    } else if (fileType === 'bolovanje') {
      this.setState({ bolovanjeFile: file });
    } else if (fileType === 'obracun') {
      this.setState({ obracunFile: file });
    }
  };

  handleSubmit = async () => {
    const { onCloseModal } = this.props;
    const { ip1File, bolovanjeFile, obracunFile } = this.state;

    // Set loading state
    this.setState({ isLoading: true });

    const formData = new FormData();

    if (ip1File) {
      formData.append('ip1', ip1File);
    }
    if (bolovanjeFile) {
      formData.append('bolovanje', bolovanjeFile);
    }
    if (obracunFile) {
      formData.append('obracun', obracunFile);
    }

    try {
      const response = await axios.post('/report/pdf_payroll_report', formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Create a download link for the zip file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payroll.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Reset form state
      this.resetForm();

      onCloseModal();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating payroll:', error);
      alert('Error generating payroll. Please try again.');
    } finally {
      // Reset loading state
      this.setState({ isLoading: false });
    }
  };

  resetForm = () => {
    this.setState({
      ip1File: null,
      bolovanjeFile: null,
      obracunFile: null,
      isLoading: false,
    });

    // Reset file input elements
    const ip1Input = document.getElementById('ip1-file') as HTMLInputElement;
    const bolovanjeInput = document.getElementById('bolovanje-file') as HTMLInputElement;
    const obracunInput = document.getElementById('obracun-file') as HTMLInputElement;

    if (ip1Input) ip1Input.value = '';
    if (bolovanjeInput) bolovanjeInput.value = '';
    if (obracunInput) obracunInput.value = '';
  };

  isFormValid = (): boolean => {
    const { ip1File, bolovanjeFile, obracunFile } = this.state;

    // At least one file must be selected
    return !ip1File && !bolovanjeFile && !obracunFile;
  };

  render(): React.ReactNode {
    const { onCloseModal, disableButtons } = this.props;
    const { isLoading, ip1File, bolovanjeFile, obracunFile } = this.state;

    return (
      <>
        <ModalHeader title="Generate Payroll" onCloseClick={onCloseModal} />
        <ModalBody>
          <div className="payroll-modal-content">
            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="loading-text">Generating payroll...</p>
                </div>
              </div>
            )}
            <form>
              <FormGroupWrapper>
                <label htmlFor="ip1-file" className="col-form-label">
                  <strong>IP1 obrasci</strong>
                </label>
                <input
                  type="file"
                  id="ip1-file"
                  className="form-control"
                  accept=".pdf"
                  onChange={this.handleFileChange('ip1')}
                  disabled={isLoading}
                />
                {ip1File && <small className="text-success">✓ {ip1File.name}</small>}
              </FormGroupWrapper>

              <FormGroupWrapper>
                <label htmlFor="obracun-file" className="col-form-label">
                  <strong>Obračuni plaća</strong>
                </label>
                <input
                  type="file"
                  id="obracun-file"
                  className="form-control"
                  accept=".pdf"
                  onChange={this.handleFileChange('obracun')}
                  disabled={isLoading}
                />
                {obracunFile && <small className="text-success">✓ {obracunFile.name}</small>}
              </FormGroupWrapper>

              <FormGroupWrapper>
                <label htmlFor="bolovanje-file" className="col-form-label">
                  <strong>Bolovanja i naknade</strong>
                </label>
                <input
                  type="file"
                  id="bolovanje-file"
                  className="form-control"
                  accept=".pdf"
                  onChange={this.handleFileChange('bolovanje')}
                  disabled={isLoading}
                />
                {bolovanjeFile && <small className="text-success">✓ {bolovanjeFile.name}</small>}
              </FormGroupWrapper>
            </form>
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalControls
            showEditButton={false}
            disableButtons={disableButtons || isLoading}
            onCloseButtonClick={onCloseModal}
            onSubmitButtonClick={this.handleSubmit}
            submitButtonText={"Generate"}
            isFormValid={this.isFormValid()}
          />
        </ModalFooter>
      </>
    );
  }
}

export default PayrollGeneratorModal;

